import React, { useState, useMemo } from 'react';
import { Clock, MapPin, Building2, Plane } from 'lucide-react';
import { format, startOfWeek, addDays, isToday, addWeeks, subWeeks, parseISO, isWithinInterval } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { OrdenTrabajoVistaDetalle } from './OrdenTrabajoVistaDetalle';
import { DocumentacionIndicator } from './DocumentacionIndicator';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';
import { ObraJornada } from '@/contexts/ObrasContext';
import { Ausencia, TIPO_AUSENCIA_LABEL, TIPO_AUSENCIA_COLOR } from '@/contexts/AusenciasContext';

interface MiCalendarioSemanaProps {
  ordenes: OrdenTrabajo[];
  jornadas?: ObraJornada[];
  ausencias?: Ausencia[];
}

const prioridadColors: Record<string, string> = {
  baja: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400',
  media: 'bg-amber-500/20 border-amber-500/50 text-amber-400',
  alta: 'bg-orange-500/20 border-orange-500/50 text-orange-400',
  urgente: 'bg-red-500/20 border-red-500/50 text-red-400',
};

const estadoColors: Record<string, string> = {
  creada: 'bg-slate-500',
  asignada: 'bg-blue-500',
  en_progreso: 'bg-amber-500',
  finalizada: 'bg-emerald-500',
  cancelada: 'bg-red-500',
};

export const MiCalendarioSemana: React.FC<MiCalendarioSemanaProps> = ({ ordenes, jornadas = [], ausencias = [] }) => {
  const isMobile = useIsMobile();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedOrden, setSelectedOrden] = useState<OrdenTrabajo | null>(null);

  // Generate week days
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  // Organize orders by date
  const ordenesByDate = useMemo(() => {
    const map: Record<string, OrdenTrabajo[]> = {};
    
    weekDays.forEach(day => {
      map[format(day, 'yyyy-MM-dd')] = [];
    });

    ordenes
      .filter(o => o.estado !== 'cancelada')
      .forEach(orden => {
        if (orden.fecha_limite) {
          const fechaKey = format(new Date(orden.fecha_limite), 'yyyy-MM-dd');
          if (map[fechaKey]) {
            map[fechaKey].push(orden);
          }
        }
      });

    // Sort orders by time within each day
    Object.keys(map).forEach(dateKey => {
      map[dateKey].sort((a, b) => {
        const timeA = a.hora_ejecucion || '23:59';
        const timeB = b.hora_ejecucion || '23:59';
        return timeA.localeCompare(timeB);
      });
    });

    return map;
  }, [ordenes, weekDays]);

  // Organize jornadas by date
  const jornadasByDate = useMemo(() => {
    const map: Record<string, ObraJornada[]> = {};
    weekDays.forEach(day => {
      map[format(day, 'yyyy-MM-dd')] = [];
    });
    jornadas.forEach(j => {
      const key = j.fecha;
      if (map[key]) map[key].push(j);
    });
    Object.keys(map).forEach(k => {
      map[k].sort((a, b) => (a.hora_inicio || '').localeCompare(b.hora_inicio || ''));
    });
    return map;
  }, [jornadas, weekDays]);

  // Organize ausencias by date (a single ausencia spans multiple days)
  const ausenciasByDate = useMemo(() => {
    const map: Record<string, Ausencia[]> = {};
    weekDays.forEach(day => {
      const key = format(day, 'yyyy-MM-dd');
      map[key] = ausencias.filter(a => {
        if (a.estado_validacion === 'rechazada') return false;
        try {
          return isWithinInterval(day, { start: parseISO(a.fecha_inicio), end: parseISO(a.fecha_fin) });
        } catch { return false; }
      });
    });
    return map;
  }, [ausencias, weekDays]);

  const formatTime = (time: string | undefined | null) => {
    if (!time) return null;
    return time.slice(0, 5);
  };

  const goToPreviousWeek = () => setCurrentWeekStart(prev => subWeeks(prev, 1));
  const goToNextWeek = () => setCurrentWeekStart(prev => addWeeks(prev, 1));
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Mobile rendering: vertical day-by-day list
  if (isMobile) {
    return (
      <div className="space-y-3">
        <div className="flex flex-col gap-2 bg-card/50 p-3 rounded-lg border border-border/50">
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="icon" onClick={goToPreviousWeek} aria-label="Semana anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goToCurrentWeek} className="gap-1">
              <Calendar className="h-4 w-4" />
              Hoy
            </Button>
            <Button variant="outline" size="icon" onClick={goToNextWeek} aria-label="Semana siguiente">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-center text-sm font-semibold">
            {format(currentWeekStart, "d MMM", { locale: es })} - {format(addDays(currentWeekStart, 6), "d MMM yyyy", { locale: es })}
          </div>
        </div>

        <div className="space-y-3">
          {weekDays.map(day => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const dayOrdenes = ordenesByDate[dateKey] || [];
            const dayJornadas = jornadasByDate[dateKey] || [];
            const dayAusencias = ausenciasByDate[dateKey] || [];
            const empty = dayOrdenes.length === 0 && dayJornadas.length === 0 && dayAusencias.length === 0;
            return (
              <div
                key={dateKey}
                className={cn(
                  "rounded-lg border bg-card/50 overflow-hidden",
                  isToday(day) && "border-primary/60 ring-1 ring-primary/30"
                )}
              >
                <div className={cn(
                  "px-3 py-2 flex items-baseline justify-between border-b",
                  isToday(day) ? "bg-primary/10" : "bg-muted/40"
                )}>
                  <div className={cn(
                    "text-sm font-semibold capitalize",
                    isToday(day) && "text-primary"
                  )}>
                    {format(day, "EEEE d 'de' MMMM", { locale: es })}
                  </div>
                  {!empty && (
                    <Badge variant="outline" className="text-[10px]">
                      {dayOrdenes.length + dayJornadas.length + dayAusencias.length}
                    </Badge>
                  )}
                </div>
                <div className="p-2 space-y-2">
                  {empty && (
                    <div className="text-center text-xs text-muted-foreground py-3 opacity-60">
                      Sin tareas
                    </div>
                  )}
                  {dayAusencias.map(a => {
                    const aprobada = a.estado_validacion === 'aprobada';
                    return (
                      <div
                        key={`a-${a.id}`}
                        className={cn(
                          "p-2.5 rounded border-l-4 text-xs bg-card",
                          aprobada ? "border-l-green-500 bg-green-500/5" : "border-l-amber-500 bg-amber-500/5"
                        )}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Plane className="h-3 w-3" />
                          <Badge className={cn("text-[10px]", TIPO_AUSENCIA_COLOR[a.tipo])}>{TIPO_AUSENCIA_LABEL[a.tipo]}</Badge>
                          <span className={cn("ml-auto text-[10px] font-semibold", aprobada ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400")}>
                            {aprobada ? '✓ Aprobada' : '⏳ Pendiente'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {dayOrdenes.map(orden => (
                    <div
                      key={orden.id}
                      onClick={() => setSelectedOrden(orden)}
                      className={cn(
                        "p-2.5 rounded border text-xs cursor-pointer active:scale-[0.99] transition",
                        prioridadColors[orden.prioridad]
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={cn("w-2 h-2 rounded-full shrink-0", estadoColors[orden.estado])} />
                        {orden.hora_ejecucion && (
                          <span className="flex items-center gap-0.5 text-[11px] font-semibold text-primary">
                            <Clock className="h-3 w-3" />
                            {formatTime(orden.hora_ejecucion)}
                          </span>
                        )}
                        <span className="font-mono text-[10px] opacity-70 ml-auto">
                          {orden.numero_ot}
                        </span>
                      </div>
                      <div className="font-medium leading-tight mb-1">{orden.titulo}</div>
                      {orden.cliente_nombre && (
                        <div className="flex items-center justify-between gap-2 text-[11px] opacity-80">
                          <span className="truncate">{orden.cliente_nombre}</span>
                          {(orden.cliente?.direccion || orden.cliente?.poblacion) && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                [orden.cliente?.direccion, orden.cliente?.codigo_postal, orden.cliente?.poblacion].filter(Boolean).join(', ')
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary shrink-0"
                            >
                              <MapPin className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      )}
                      <DocumentacionIndicator
                        partesCount={orden.partes_count || 0}
                        jornadasCount={orden.jornadas_count || 0}
                        estado={orden.estado}
                        variant="compact"
                        className="mt-1"
                      />
                    </div>
                  ))}
                  {dayJornadas.map(j => {
                    const horas = j.horas_trabajadas ? Number(j.horas_trabajadas).toFixed(1) : null;
                    const titulo = j.obra?.nombre || j.orden_trabajo?.titulo || j.cliente?.nombre || 'Jornada';
                    return (
                      <div
                        key={`j-${j.id}`}
                        className="p-2.5 rounded border text-xs bg-cyan-500/10 border-cyan-500/40 text-cyan-300"
                      >
                        <div className="flex items-center gap-1 mb-1 text-[11px] font-semibold">
                          <Clock className="h-3 w-3" />
                          {j.hora_inicio?.slice(0, 5)}
                          {j.hora_fin && ` - ${j.hora_fin.slice(0, 5)}`}
                          {horas && (
                            <span className="ml-auto bg-cyan-500/30 rounded px-1.5">{horas}h</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Building2 className="h-3 w-3 shrink-0" />
                          <span className="truncate font-medium">{titulo}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {selectedOrden && (
          <OrdenTrabajoVistaDetalle
            orden={selectedOrden}
            isOpen={!!selectedOrden}
            onClose={() => setSelectedOrden(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Week Navigation */}
      <div className="flex items-center justify-between bg-card/50 p-3 rounded-lg border border-border/50">
        <Button variant="outline" size="sm" onClick={goToPreviousWeek}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Anterior
        </Button>
        
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goToCurrentWeek}>
            <Calendar className="h-4 w-4 mr-2" />
            Hoy
          </Button>
          <span className="font-semibold text-foreground">
            {format(currentWeekStart, "d 'de' MMMM", { locale: es })} - {format(addDays(currentWeekStart, 6), "d 'de' MMMM yyyy", { locale: es })}
          </span>
        </div>

        <Button variant="outline" size="sm" onClick={goToNextWeek}>
          Siguiente
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Week Grid */}
      <div className="border border-border rounded-lg overflow-hidden bg-card/30">
        {/* Header Row - Days */}
        <div className="grid grid-cols-7 bg-muted/50 border-b border-border">
          {weekDays.map((day, index) => (
            <div 
              key={index} 
              className={cn(
                "p-3 text-center border-r border-border last:border-r-0",
                isToday(day) && "bg-primary/10"
              )}
            >
              <div className={cn(
                "text-xs uppercase tracking-wide",
                isToday(day) ? "text-primary font-semibold" : "text-muted-foreground"
              )}>
                {format(day, 'EEEE', { locale: es })}
              </div>
              <div className={cn(
                "text-2xl font-bold",
                isToday(day) ? "text-primary" : "text-foreground"
              )}>
                {format(day, 'd')}
              </div>
              <div className="text-xs text-muted-foreground">
                {format(day, 'MMM', { locale: es })}
              </div>
            </div>
          ))}
        </div>

        {/* Orders Grid */}
        <ScrollArea className="max-h-[500px]">
          <div className="grid grid-cols-7 min-h-[200px]">
            {weekDays.map((day, dayIndex) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayOrdenes = ordenesByDate[dateKey] || [];
              const dayJornadas = jornadasByDate[dateKey] || [];
              const dayAusencias = ausenciasByDate[dateKey] || [];

              return (
                <div
                  key={dateKey}
                  className={cn(
                    "p-2 border-r border-border last:border-r-0 min-h-[200px]",
                    isToday(day) && "bg-primary/5"
                  )}
                >
                  <div className="space-y-2">
                    {dayAusencias.map(a => {
                      const aprobada = a.estado_validacion === 'aprobada';
                      return (
                        <div
                          key={`a-${a.id}`}
                          className={cn(
                            "p-1.5 rounded border-l-4 text-[10px] bg-card",
                            aprobada ? "border-l-green-500 bg-green-500/10" : "border-l-amber-500 bg-amber-500/10"
                          )}
                          title={`${TIPO_AUSENCIA_LABEL[a.tipo]} · ${a.estado_validacion}`}
                        >
                          <div className="flex items-center gap-1">
                            <Plane className="h-3 w-3 shrink-0" />
                            <span className="truncate font-semibold">{TIPO_AUSENCIA_LABEL[a.tipo]}</span>
                          </div>
                          <div className={cn("text-[9px] font-semibold", aprobada ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400")}>
                            {aprobada ? '✓ Aprobada' : '⏳ Pendiente'}
                          </div>
                        </div>
                      );
                    })}
                    {dayOrdenes.map((orden) => (
                      <div
                        key={orden.id}
                        onClick={() => setSelectedOrden(orden)}
                        className={cn(
                          "p-2 rounded border text-xs cursor-pointer transition-all hover:scale-[1.02] hover:shadow-md",
                          prioridadColors[orden.prioridad]
                        )}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <div className={cn(
                            "w-2 h-2 rounded-full flex-shrink-0",
                            estadoColors[orden.estado]
                          )} />
                          {orden.hora_ejecucion && (
                            <span className="flex items-center gap-0.5 text-[10px] font-semibold text-primary">
                              <Clock className="h-3 w-3" />
                              {formatTime(orden.hora_ejecucion)}
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] opacity-70 mb-0.5">
                          {orden.numero_ot}
                        </div>
                        <div className="font-medium truncate leading-tight">
                          {orden.titulo}
                        </div>
                        {orden.obra && (
                          <div className="text-[10px] bg-emerald-500/20 text-emerald-400 rounded px-1 mt-1 truncate">
                            📁 {orden.obra.nombre}
                          </div>
                        )}
                        {orden.cliente_nombre && (
                          <div className="flex items-center justify-between text-[10px] opacity-70 mt-1">
                            <span className="truncate">{orden.cliente_nombre}</span>
                            {(orden.cliente?.direccion || orden.cliente?.poblacion) && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                  [orden.cliente?.direccion, orden.cliente?.codigo_postal, orden.cliente?.poblacion]
                                    .filter(Boolean)
                                    .join(', ')
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-primary hover:text-primary/80 flex-shrink-0 ml-1"
                                title="Abrir en Google Maps"
                              >
                                <MapPin className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        )}
                        <DocumentacionIndicator
                          partesCount={orden.partes_count || 0}
                          jornadasCount={orden.jornadas_count || 0}
                          estado={orden.estado}
                          variant="compact"
                          className="mt-1"
                        />
                      </div>
                    ))}

                    {/* Jornadas registradas (horas en obra/cliente) */}
                    {dayJornadas.map((j) => {
                      const horas = j.horas_trabajadas
                        ? Number(j.horas_trabajadas).toFixed(1)
                        : null;
                      const titulo = j.obra?.nombre
                        || j.orden_trabajo?.titulo
                        || j.cliente?.nombre
                        || 'Jornada';
                      return (
                        <div
                          key={`j-${j.id}`}
                          className="p-2 rounded border text-xs bg-cyan-500/10 border-cyan-500/40 text-cyan-300"
                          title={j.descripcion_trabajo || titulo}
                        >
                          <div className="flex items-center gap-1 mb-1 text-[10px] font-semibold">
                            <Clock className="h-3 w-3" />
                            {j.hora_inicio?.slice(0, 5)}
                            {j.hora_fin && ` - ${j.hora_fin.slice(0, 5)}`}
                            {horas && (
                              <span className="ml-auto bg-cyan-500/30 rounded px-1">
                                {horas}h
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 truncate">
                            <Building2 className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate font-medium">{titulo}</span>
                          </div>
                        </div>
                      );
                    })}

                    {dayOrdenes.length === 0 && dayJornadas.length === 0 && dayAusencias.length === 0 && (
                      <div className="text-center text-muted-foreground text-xs py-8 opacity-50">
                        Sin tareas
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Vista Detalle Dialog */}
      {selectedOrden && (
        <OrdenTrabajoVistaDetalle
          orden={selectedOrden}
          isOpen={!!selectedOrden}
          onClose={() => setSelectedOrden(null)}
        />
      )}
    </div>
  );
};
